import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, ArrowRight, Download, LogOut, Clock, Check, Award, Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Reveal } from "@/components/Reveal";
import { ACADEMY_MATERIALS } from "@/data/site";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SCHEDULE_STEPS = [1, 2, 3, 4];
const INCLUSIVE_ITEMS = [1, 2, 3, 4, 5, 6];

const readStoredAccess = () => {
  try {
    return JSON.parse(sessionStorage.getItem("academy_access"));
  } catch {
    return null;
  }
};

const SectionHead = ({ title }) => (
  <div className="flex items-baseline gap-4 mb-5">
    <h2 className="font-serif text-2xl md:text-3xl font-light">{title}</h2>
    <span className="flex-1 border-b border-primary/30" />
  </div>
);

export default function Academy() {
  const { t, lang } = useLang();
  const [access, setAccess] = useState(readStoredAccess);
  const [code, setCode] = useState("");
  const [showCode, setShowCode] = useState(false);
  const [loading, setLoading] = useState(false);

  const locale = lang === "ru" ? "ru-RU" : lang === "en" ? "en-GB" : "de-DE";
  const fmtDate = (iso) => {
    try {
      return new Date(`${iso}T12:00:00`).toLocaleDateString(locale, { day: "2-digit", month: "long", year: "numeric" });
    } catch {
      return iso;
    }
  };

  const unlock = async (e) => {
    e.preventDefault();
    if (!code.trim()) return toast.error(t("portal.err_code"));
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/academy/unlock`, { code: code.trim() });
      sessionStorage.removeItem("academy_unlocked");
      sessionStorage.setItem("academy_access", JSON.stringify(data));
      setAccess(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 403 && detail?.error === "not_yet") {
        toast.error(t("portal.err_not_yet").replace("{date}", fmtDate(detail.date)), { duration: 6000 });
      } else if (err?.response?.status === 403 && detail?.error === "expired") {
        toast.error(t("portal.err_expired"), { duration: 6000 });
      } else {
        toast.error(t("portal.err_code"));
      }
    } finally {
      setLoading(false);
    }
  };

  const lock = () => {
    sessionStorage.removeItem("academy_access");
    sessionStorage.removeItem("academy_unlocked");
    setAccess(null);
    setCode("");
  };

  if (!access) {
    return (
      <div data-testid="academy-locked" className="min-h-[80vh] flex items-center justify-center px-5 pt-24 pb-20">
        <Reveal className="w-full max-w-md text-center">
          <div className="mx-auto w-12 h-12 border border-primary/40 flex items-center justify-center text-primary">
            <Lock strokeWidth={1.25} size={20} />
          </div>
          <span className="block mt-6 text-xs uppercase tracking-[0.3em] text-muted-foreground">{t("portal.eyebrow")}</span>
          <h1 className="font-serif font-light text-4xl md:text-5xl tracking-tight mt-4">{t("portal.title")}</h1>
          <p className="mt-5 text-sm text-foreground/60 leading-relaxed max-w-sm mx-auto">{t("portal.lock_body")}</p>
          <form onSubmit={unlock} className="mt-8 flex gap-3">
            <div className="relative flex-1">
              <Input
                data-testid="academy-code-input"
                type={showCode ? "text" : "password"}
                autoComplete="off"
                placeholder={t("portal.code_placeholder")}
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                className="h-14 rounded-none pr-12"
              />
              <button
                type="button"
                data-testid="academy-code-toggle"
                onClick={() => setShowCode((s) => !s)}
                aria-label={showCode ? "Code verbergen" : "Code anzeigen"}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground/40 transition-colors hover:text-primary"
              >
                {showCode ? <EyeOff strokeWidth={1.5} size={18} /> : <Eye strokeWidth={1.5} size={18} />}
              </button>
            </div>
            <button
              type="submit"
              data-testid="academy-unlock-btn"
              disabled={loading}
              className="group inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 disabled:opacity-60 transition-opacity hover:opacity-90"
            >
              <span className="text-sm tracking-wide whitespace-nowrap">{loading ? "…" : t("portal.unlock")}</span>
              <ArrowRight strokeWidth={1.5} size={16} />
            </button>
          </form>
        </Reveal>
      </div>
    );
  }

  return (
    <div data-testid="academy-portal" className="pt-32 pb-24 md:pb-32">
      <div className="mx-auto max-w-[900px] px-5 md:px-10">
        <Reveal>
          <span className="text-xs uppercase tracking-[0.25em] text-muted-foreground">{t("portal.eyebrow")}</span>
          <h1 className="font-serif font-light text-4xl md:text-6xl tracking-tight mt-4">
            {access.name ? t("portal.hello").replace("{name}", access.name) : t("portal.welcome")}
          </h1>
          <p className="mt-5 text-foreground/70 leading-relaxed max-w-xl">{t("portal.body")}</p>
        </Reveal>

        <div className="mt-16 space-y-16">
          {/* Ablauf */}
          <Reveal>
            <section data-testid="portal-sec-ablauf">
              <SectionHead title={t("portal.sec_ablauf")} />
              <ol className="divide-y divide-border border-y border-border">
                {SCHEDULE_STEPS.map((n) => (
                  <li key={n} data-testid={`portal-step-${n}`} className="flex gap-5 py-5">
                    <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center border border-primary/40 font-serif text-sm text-primary">
                      {n}
                    </span>
                    <div>
                      <p className="text-sm font-medium tracking-wide">{t(`portal.ablauf_t${n}`)}</p>
                      <p className="mt-1 text-sm text-foreground/60 leading-relaxed">{t(`portal.ablauf_d${n}`)}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>
          </Reveal>

          {/* Inklusive */}
          <Reveal>
            <section data-testid="portal-sec-inklusive">
              <SectionHead title={t("portal.sec_inklusive")} />
              <ul className="grid gap-x-10 gap-y-3 sm:grid-cols-2">
                {INCLUSIVE_ITEMS.map((n) => (
                  <li key={n} data-testid={`portal-inkl-${n}`} className="flex items-start gap-3 text-sm text-foreground/75">
                    <Check strokeWidth={1.5} size={16} className="mt-0.5 shrink-0 text-primary" />
                    <span>{t(`portal.inkl_${n}`)}</span>
                  </li>
                ))}
              </ul>
            </section>
          </Reveal>

          {/* Schulungsunterlagen */}
          <Reveal>
            <section data-testid="portal-sec-materialien">
              <SectionHead title={t("portal.sec_materialien")} />
              <p className="mb-4 flex items-start gap-2.5 text-sm text-foreground/60">
                <Clock strokeWidth={1.5} size={15} className="mt-0.5 shrink-0 text-primary" />
                {t("portal.materialien_hint")}
              </p>
              {ACADEMY_MATERIALS.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">{t("portal.empty")}</p>
              ) : (
                <ul className="divide-y divide-border border-y border-border">
                  {ACADEMY_MATERIALS.map((m) => (
                    <li key={m.href}>
                      <a
                        href={m.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="portal-file-link"
                        className="group flex items-center justify-between gap-4 py-4 text-sm transition-colors hover:text-primary"
                      >
                        <span>{t(m.labelKey)}</span>
                        <Download strokeWidth={1.5} size={16} className="text-primary shrink-0" />
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </Reveal>

          {/* Zertifikate */}
          <Reveal>
            <section data-testid="portal-sec-zertifikate">
              <SectionHead title={t("portal.sec_zertifikate")} />
              <div className="border border-primary/30 bg-muted px-6 py-6">
                <div className="flex items-start gap-4">
                  <Award strokeWidth={1.25} size={22} className="mt-0.5 shrink-0 text-primary" />
                  <div>
                    <p className="text-sm text-foreground/75 leading-relaxed">{t("portal.zert_body")}</p>
                    <ul className="mt-4 space-y-3">
                      {[1, 2].map((n) => (
                        <li key={n} data-testid={`portal-zert-${n}`} className="flex gap-4 text-sm text-foreground/75 leading-relaxed">
                          <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center border border-primary/40 font-serif text-xs text-primary">{n}</span>
                          <span>{t(`portal.zert_${n}`)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </section>
          </Reveal>
        </div>

        <button
          onClick={lock}
          data-testid="portal-lock-btn"
          className="mt-14 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground transition-colors hover:text-primary"
        >
          <LogOut strokeWidth={1.5} size={14} />
          {t("portal.lock_again")}
        </button>
      </div>
    </div>
  );
}
